import React, { forwardRef, ReactElement, useMemo } from 'react';
import { css } from '@emotion/react';
import {
  dropdown_filter_label_error_style,
  dropdown_filter_label_selected_style,
  dropdown_label_base_css,
  getDropdownFilterLabelSizeStyle,
  getDropdownFilterLabelSpacingBySize,
  getDropdownItemFontStyle,
} from './styles';
import { getInputSizeStyle, input_disabled_style, input_error_style } from '../input/styles';
import { DropdownArrowButton } from './DropdownArrowButton';
import { buildCSSWithLength } from '../../styles';
import { Stack } from '../stack';
import { IconProps } from '../icons/generated';
import { ButtonIcon } from '../button/ButtonIcon';
import { semantic_colors } from '../../foundation';
import { DropdownLabelProps } from './DropdownLabel';

export interface DropdownFilterLabelProps extends DropdownLabelProps {
  startIcon?: ReactElement<IconProps>;
}

export const DropdownFilterLabel = forwardRef<HTMLDivElement, DropdownFilterLabelProps>(
  (
    { width, disabled, expanded = false, selected = false, status, children, size = 'medium', startIcon, ...props },
    ref,
  ) => {
    const propsStyle = {
      ...(selected && dropdown_filter_label_selected_style),
      ...(status === 'error' && dropdown_filter_label_error_style),
      ...(disabled && input_disabled_style),
    };

    const currentLabelColor = useMemo(() => {
      return disabled
        ? semantic_colors.content.disabled
        : status === 'error'
        ? semantic_colors.state.negative
        : selected
        ? semantic_colors.accent.primary
        : startIcon?.props.color ?? semantic_colors.content.primary;
    }, [status, selected, startIcon]);
    const labelGap = getDropdownFilterLabelSpacingBySize(size);

    return (
      <div
        role='button'
        css={[
          dropdown_label_base_css,
          getInputSizeStyle(size),
          getDropdownItemFontStyle(size),
          propsStyle,
          css`
            ${buildCSSWithLength('width', width ?? 'max-content')}
            cursor: ${disabled ? 'not-allowed' : 'pointer'};
            color: ${currentLabelColor};
          `,
          getDropdownFilterLabelSizeStyle(size),
          status === 'error' && input_error_style,
        ]}
        ref={ref}
        {...props}
      >
        <Stack direction='row' as='div' gap={labelGap} align='center'>
          {startIcon && (
            <ButtonIcon size={startIcon.props.size ?? 16} color={currentLabelColor}>
              {startIcon}
            </ButtonIcon>
          )}
          <span>{children}</span>
        </Stack>
        <Stack direction='row' gap={labelGap} align='center'>
          <DropdownArrowButton expanded={expanded} colorProps={currentLabelColor} />
        </Stack>
      </div>
    );
  },
);
